import React, { FC, useEffect } from 'react';

import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { FormikProps } from 'formik';

import { HelpTypes, pharmagapTransactionFilters, searchedPharmacies, searchedPharmacists, TypeOfUser } from '@pharmaplan/common';
import { dropdownMapper } from '@pharmaplan/common/helpers/utils';

import useAutoCompleteScroll, { AutoCompleteScrollTypes } from '../../../../hooks/useAutoCompleteScroll';
import { getDisabledSubmitFilter } from '../../../../selectors/calendarSelector';
import FilterSubmitButtons from '../../../common/FilterSubmitButtons';
import CustomAutocomplete from '../../../common/CustomAutocomplete';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { toOptions } from '../../ExpressBooking/ExpressBooking';
import useAutoComplete from '../../../../hooks/useAutocomplete';
import CustomDateRange from '../../../common/CustomDateRange';
import CustomDropdown from '../../../common/CustomDropdown';
import strings from '../../../../localization';

import styles from './styles';
import { nameLabels } from '../../../../helpers/Constants';

interface IPharmagapTransactionHeader {
  formik: FormikProps<any>;
  handleReset: () => void;
}

const PharmagapTransactionHeader: FC<IPharmagapTransactionHeader> = ({
  formik,
  handleReset,
}) => {
  const disableSubmit = useAppSelector(getDisabledSubmitFilter);
  const filters = useAppSelector(pharmagapTransactionFilters);
  const { transactionStatuses, transactionTypes } = filters ?? {};
  const pharmacies = useAppSelector(searchedPharmacies);
  const pharmacists = useAppSelector(searchedPharmacists);

  const pharmacyOptions = toOptions(pharmacies);
  const pharmacistOptions = toOptions(pharmacists);

  const { debouncedInput: pharmacyInput } = useAutoComplete({
    userTypes: [HelpTypes.pharmacy],
  });
  const { debouncedInput: pharmacistInput } = useAutoComplete({
    userTypes: [HelpTypes.pharmacist],
  });

  const { onEndReached, pharmacyListData, pharmacistListData } = useAutoCompleteScroll(
    AutoCompleteScrollTypes.all,
  );

  const { pharmacies: formikPharmacies, pharmacists: formikPharmacists } = formik.values;
  const pharmacyOption = formikPharmacies ? pharmacyOptions : pharmacyListData;
  const pharmacistOption = formikPharmacists ? pharmacistOptions : pharmacistListData;

  useEffect(() => {
    pharmacyInput(formikPharmacies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikPharmacies]);

  useEffect(() => {
    pharmacistInput(formikPharmacists);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikPharmacists]);

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <Typography sx={styles.title}>{strings.pharmagapTransactions}</Typography>
      <Grid container lg={10} alignItems="center" xs={12} wrap="nowrap">
        <CustomDateRange
          startName="startDate"
          endName="endDate"
          formik={formik}
          preventPast={false}
        />

        <Grid xs={6} sx={styles.autocomplete}>
          <CustomAutocomplete
            formik={formik}
            options={pharmacistOption}
            textName="pharmacists"
            autoCompleteName="pharmacistSelected"
            idName="pharmacistId"
            label={nameLabels().roleLabel}
            placeholder={
              strings.formatString(
                strings.enterCred,
                `${strings.name}/${strings.email}`,
              ) as string
            }
            infiniteScroll={!formikPharmacists}
            onEndReached={onEndReached(TypeOfUser.pharmacist)}
          />
        </Grid>
        <Grid xs={6} sx={styles.autocomplete}>
          <CustomAutocomplete
            formik={formik}
            options={pharmacyOption}
            textName="pharmacies"
            autoCompleteName="pharmacySelected"
            idName="pharmacyId"
            label={strings.pharmacy}
            placeholder={
              strings.formatString(
                strings.enterCred,
                `${strings.name}/${strings.email}`,
              ) as string
            }
            infiniteScroll={!formikPharmacies}
            onEndReached={onEndReached(TypeOfUser.pharmacy)}
          />
        </Grid>
        <Grid xs={6} sx={styles.autoCompleteContainer}>
          <CustomDropdown
            formik={formik}
            name="transactionType"
            label={strings.transactionType}
            menuItems={dropdownMapper(transactionTypes, 'key', 'value')}
          />
        </Grid>
        <Grid xs={6} sx={styles.autoCompleteContainer}>
          <CustomDropdown
            formik={formik}
            name="transactionStatus"
            label={strings.transactionStatus}
            menuItems={dropdownMapper(transactionStatuses, 'key', 'value')}
          />
        </Grid>

        <FilterSubmitButtons
          handleReset={handleReset}
          disableSubmit={disableSubmit}
        />
      </Grid>
    </Box>
  );
};

export default PharmagapTransactionHeader;
